import React, {Component} from 'react';
import {connect, Provider} from 'react-redux';
import {Link, NavLink} from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import * as actionCreators from '../store/actions';
import historyComponent from './hoc/historyComponent';
import LegalPopup from './Elements/LegalPopup';
import FeedbackPopup from './Elements/FeedbackPopup';
import store from '../store/store';
import utilities from '../utilities/utilities';
import Logo from './Elements/Logo';
import {
  getQueryParam, isPropertyShortPath, parsePropertyAlias, prepareUrl, preserveMapViewSearch,
} from '../helpers/common';
import renderPopup from '../helpers/renderPopup';
import {renderReactNode} from '../helpers/reactDomCompat';
import OnBoardingPopup from './Elements/OnBoardingPopup';
import ResultsInfoHeader from './Containers/Map/Elements/PropertyPanel/Elements/ResultsInfoHeader';
import {BASE_URL_TYPE} from '../api/axios';
import {sitemapLink, sitemapTitle} from './Containers/Sitemap';
import buildBrowseHrefFromLocation from './Containers/Browse/browseHrefFromLocation';

const SiteUrl = BASE_URL_TYPE.PROPZONE;
const MAP_RETURN_URL_STORAGE_KEY = 'map-return-url';

function getLocationUrl(location) {
  return `${location.pathname || ''}${location.search || ''}${location.hash || ''}` || '/';
}

function isPropertyDetailsLocation(location) {
  if (isPropertyShortPath(location.pathname)) {
    return true;
  }
  const params = new URLSearchParams(location.search || '');
  return !!(params.get('folio') || params.get('property_id'));
}

function rememberMapReturnUrl(location) {
  try {
    if (isPropertyDetailsLocation(location)) {
      sessionStorage.setItem(MAP_RETURN_URL_STORAGE_KEY, getLocationUrl(location));
    }
    else {
      sessionStorage.removeItem(MAP_RETURN_URL_STORAGE_KEY);
    }
  }
  catch (error) {
    _.noop(error);
    // Ignore unavailable storage; navigation still falls back to city-search.
  }
}

function getMapReturnUrl() {
  try {
    return sessionStorage.getItem(MAP_RETURN_URL_STORAGE_KEY);
  }
  catch (error) {
    _.noop(error);
    return null;
  }
}

function normalizeMapPath(path) {
  if (!path || isPropertyShortPath(path)) {
    return '';
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return isPropertyShortPath(normalizedPath) ? '' : normalizedPath;
}

function getMarketMapPath(navigationPlace) {
  const placePath = normalizeMapPath(navigationPlace?.url || navigationPlace?.path || '');
  if (placePath) {
    return placePath;
  }
  try {
    return normalizeMapPath(localStorage.getItem('city-search') || '');
  }
  catch (error) {
    _.noop(error);
    return '';
  }
}

const propTypes = {
  userData: PropTypes.instanceOf(Object).isRequired,
  pageData: PropTypes.instanceOf(Object),
  history: PropTypes.instanceOf(Object).isRequired,
  location: PropTypes.instanceOf(Object).isRequired,
  mapSearchResults: PropTypes.instanceOf(Object).isRequired,
  mapData: PropTypes.instanceOf(Object).isRequired,
  toggleLeftOverlay: PropTypes.func.isRequired,
  enabledLeftOverlay: PropTypes.string.isRequired,
  doc: PropTypes.string.isRequired,
  isActive: PropTypes.func.isRequired,
  isDocView: PropTypes.func.isRequired,
  isCrm: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  toggleMenuCollapse: PropTypes.func.isRequired,
  menuCollapsed: PropTypes.bool.isRequired,
  openedPropertyState: PropTypes.number.isRequired,
  navigationPlace: PropTypes.instanceOf(Object),
};

const defaultProps = {
  pageData: {},
  navigationPlace: null,
};

const resizeEvent = new CustomEvent('resize');

export class LeftMenu extends Component {
  static openFeedbackPopup(e) {
    e.preventDefault();
    renderReactNode(
      <Provider store={store}>
        <FeedbackPopup />
      </Provider>,
      document.getElementById('popup-container'),
    );
  }

  constructor(props) {
    super(props);

    document.body.classList.add('app-nav');

    this.currentHost = BASE_URL_TYPE.PROPZONE.replace(/https:\/\/|http:\/\//, '');

    this.state = {
      showGridicsMenu: false,
    };

    this.toggleLink = this.toggleLink.bind(this);
    this.documentViewClick = this.documentViewClick.bind(this);
    this.openLegalPopup = this.openLegalPopup.bind(this);
    this.toggleGridicsMenu = this.toggleGridicsMenu.bind(this);
    this.closeGridicsMenu = this.closeGridicsMenu.bind(this);

    this.onBoardInited = false;

    document.addEventListener('click', this.closeGridicsMenu);
  }

  componentDidUpdate(prevProps) {
    // Show legal disclaimer or onboarding only when user is not in the middle of subscription checkout.
    const {userData, menuCollapsed} = this.props;
    const sku = getQueryParam('sku');
    const subscriptionPopup = getQueryParam('subscriptionPopup');
    const deferPopupsForSubscription = !!(sku || subscriptionPopup);

    if (utilities.isLoggedIn(userData) && !deferPopupsForSubscription) {
      const hosts = _.get(userData, 'legal_disclaimer.hosts') || [];
      if (!_.includes(hosts, this.currentHost)) {
        this.openLegalPopup();
      }
      else if (!this.onBoardInited && !_.get(userData, 'onboard_disabled', false)) {
        renderPopup(<OnBoardingPopup />);
        this.onBoardInited = true;
      }
    }
    else if (utilities.isLoggedIn(userData) && deferPopupsForSubscription && !this.onBoardInited) {
      this.onBoardInited = true;
    }

    if (prevProps.menuCollapsed !== menuCollapsed) {
      this.animationEndTimeout = setTimeout(() => {
        window.dispatchEvent(resizeEvent);
      }, 300);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.closeGridicsMenu);
    document.body.classList.remove('app-nav');
  }

  getShortPropertyMarketUrl(leftOverlay) {
    const {location, navigationPlace} = this.props;
    if (!isPropertyShortPath(location.pathname)) {
      return null;
    }
    const marketPath = getMarketMapPath(navigationPlace);
    const parsed = parsePropertyAlias(location.pathname.replace('/zoning-detail/', ''));
    if (!marketPath || !parsed?.folio) {
      return null;
    }
    const normalizedMarketPath = marketPath.startsWith('/') ? marketPath : `/${marketPath}`;
    // Carry the applied filters/view state over from the short path; seeding from an empty
    // URLSearchParams would drop `_tags_*` filters when jumping to the market Filters/Layers panel.
    const params = new URLSearchParams(preserveMapViewSearch(location.search).replace(/^\?/, ''));
    params.set('leftOverlay', leftOverlay);
    params.set('folio', parsed.folio);
    return `${normalizedMarketPath}?${params.toString()}${location.hash || ''}`;
  }

  toggleLink(e, name) {
    const {toggleLeftOverlay} = this.props;
    if (e) {
      e.preventDefault();
    }
    toggleLeftOverlay(name);
  }

  documentViewClick() {
    this.toggleLink(null, '');
  }

  openLegalPopup(e) {
    if (e) {
      e.preventDefault();
    }
    // Show popup with legal disclaimer notice.
    renderReactNode(
      <Provider store={store}>
        <LegalPopup {...this.props} />
      </Provider>,
      document.getElementById('popup-container'),
    );
  }

  toggleGridicsMenu(e) {
    e.preventDefault();
    const {showGridicsMenu} = this.state;
    this.setState({showGridicsMenu: !showGridicsMenu});
  }

  closeGridicsMenu(e) {
    const {showGridicsMenu} = this.state;
    if (showGridicsMenu && !e.target.closest('.toogle-menu')) {
      this.setState({showGridicsMenu: false});
    }
  }

  userBlock() {
    const {userData} = this.props;
    if (utilities.isLoggedIn(userData)) {
      const userName = `${userData.field_user_first_name} ${userData.field_user_last_name}`;
      return (
        <div className="user-welcome">
          <div className="welcome">
            <div>Welcome</div>
            <span className="user">{userName}</span>
          </div>
        </div>
      );
    }
    return '';
  }

  linkClick(e, name) {
    const {pageData} = this.props;
    let event = e;
    if (!_.isEmpty(pageData)) {
      e.preventDefault();
    }
    else {
      event = null;
    }
    this.toggleLink(event, name);
  }

  navCount() {
    const {isMobile, mapData, mapSearchResults} = this.props;
    if (!isMobile && !_.isEmpty(mapData)) {
      return <ResultsInfoHeader className="nav-count" showTitle={false} count={mapSearchResults.filters.count || ''} />;
    }
    return '';
  }

  render() {
    const {
      enabledLeftOverlay,
      toggleLeftOverlay,
      isMobile,
      toggleMenuCollapse,
      menuCollapsed,
      openedPropertyState,
      history,
      location,
      navigationPlace,
    } = this.props;
    const {showGridicsMenu} = this.state;
    const marketingSiteUrl = 'https://www.gridics.com';
    const logoSrc = `${SiteUrl}/themes/custom/gridics/images/gridics-menu.svg`;
    const marketPath = getMarketMapPath(navigationPlace);
    const mapTabLink = marketPath || '/';
    const allPropertiesHref = buildBrowseHrefFromLocation(location, marketPath);
    const crmCleanupParams = [
      'leftOverlay',
      'activeTab',
      'propertyActiveTab',
      'folio',
      'property_id',
    ];
    const myPropertiesUrl = prepareUrl('/crm/properties', {}, crmCleanupParams) || '/crm/properties';
    const notesUrl = prepareUrl('/crm/notes', {}, crmCleanupParams) || '/crm/notes';

    return (
      <div className="ch-menu">
        {!isMobile && (
          <div className="top">
            <button
              type="button"
              className="toggler no-style"
              aria-label="Left Menu"
              onClick={(e) => {
                e.preventDefault();
                toggleMenuCollapse(!menuCollapsed);
                document.body.classList.toggle('menu-collapsed', !menuCollapsed);
              }}
            >
              <i className="icon-b icon-b-burger" />
            </button>
            <Logo />
          </div>
        )}
        <div className="menu-inner">
          {!menuCollapsed && this.navCount()}
          {!isMobile && this.userBlock()}
          {menuCollapsed && !isMobile && <Logo />}
          {menuCollapsed && this.navCount()}
          <nav className="ch-top-menu">
            <Link
              title="Filters"
              to={mapTabLink}
              className={classnames('search', {active: enabledLeftOverlay === 'showFilters'})}
              onClick={(e) => {
                // Already on this panel: toggle it closed by clearing the overlay from the URL.
                // Driving the close through the URL (not a direct redux toggle) keeps redux and
                // the URL in sync, so UrlQueryApplier does not re-open it and flicker the panel.
                if (enabledLeftOverlay === 'showFilters') {
                  e.preventDefault();
                  history.push(prepareUrl(mapTabLink, {}, ['leftOverlay', 'activeTab', 'propertyActiveTab']) || mapTabLink);
                  return;
                }
                const newUrl = this.getShortPropertyMarketUrl('filters')
                  || prepareUrl(
                    mapTabLink,
                    {
                      leftOverlay: 'filters',
                    },
                    ['activeTab', 'propertyActiveTab'],
                  );
                if (newUrl) {
                  e.preventDefault();
                  history.push(newUrl);
                }
                else {
                  this.linkClick(e, 'showFilters');
                }
              }}
            >
              <i className="icon-b icon-b-search-home" />
              <span className="title">Filters</span>
            </Link>
            <Link
              title="Properties"
              to={mapTabLink}
              className={classnames('analyze', {
                active: _.includes(['showResults', 'showProperties'], enabledLeftOverlay),
              })}
              onClick={(e) => {
                const returnUrl = getMapReturnUrl();
                if (returnUrl && location.pathname.indexOf('/crm/') === 0) {
                  e.preventDefault();
                  history.push(returnUrl);
                  return;
                }
                // Already on this panel: hide it but KEEP the property data (folio/property_id,
                // propertyPopupData, openedPropertyState) so re-clicking re-opens the SAME property.
                // This is a redux-only toggle (no URL change), so UrlQueryApplier neither re-opens
                // nor clears the property — which is exactly why it must not go through the URL here.
                if (_.includes(['showResults', 'showProperties'], enabledLeftOverlay)) {
                  this.linkClick(e, enabledLeftOverlay);
                  return;
                }
                if (openedPropertyState === 1 && isPropertyShortPath(location.pathname)) {
                  e.preventDefault();
                  toggleLeftOverlay('showProperties', true, false);
                  return;
                }
                const panelToShow = openedPropertyState === 1 ? 'properties' : 'results';
                const newUrl = prepareUrl(
                  mapTabLink,
                  {
                    leftOverlay: panelToShow,
                  },
                  ['activeTab', 'propertyActiveTab'],
                );
                if (newUrl) {
                  e.preventDefault();
                  history.push(newUrl);
                }
                else {
                  this.linkClick(e, openedPropertyState === 1 ? 'showProperties' : 'showResults');
                }
              }}
            >
              <i className="icon-b icon-b-properties" />
              <span className="title">Properties</span>
            </Link>
            <Link
              title="Layers"
              to={mapTabLink}
              className={classnames('map-btn-switch layers-toggle', {
                active: enabledLeftOverlay === 'showLayers',
              })}
              onClick={(e) => {
                // Already on this panel: toggle it closed via the URL (see Filters note above).
                if (enabledLeftOverlay === 'showLayers') {
                  e.preventDefault();
                  history.push(prepareUrl(mapTabLink, {}, ['leftOverlay', 'activeTab', 'propertyActiveTab']) || mapTabLink);
                  return;
                }
                const newUrl = this.getShortPropertyMarketUrl('layers')
                  || prepareUrl(
                    mapTabLink,
                    {
                      leftOverlay: 'layers',
                    },
                    ['activeTab', 'propertyActiveTab'],
                  );
                if (newUrl) {
                  e.preventDefault();
                  history.push(newUrl);
                }
                else {
                  this.linkClick(e, 'showLayers');
                }
              }}
            >
              <i className="icon-b icon-b-layers" />
              <span className="title">Layers</span>
            </Link>
            <NavLink
              title="My Properties"
              to={myPropertiesUrl}
              onClick={() => {
                rememberMapReturnUrl(location);
              }}
            >
              <i className="icon-b icon-b-heart" />
              <span className="title">My Properties</span>
            </NavLink>
            <NavLink
              title="Notes"
              to={notesUrl}
              state={{cacheReset: true}}
              onClick={() => {
                rememberMapReturnUrl(location);
              }}
            >
              <i className="icon-b icon-b-notes" />
              <span className="title">Notes</span>
            </NavLink>
          </nav>
        </div>
        <nav className="ch-bottom-menu">
          {!isMobile && (
            <a href={sitemapLink}>
              <span className="title">{sitemapTitle}</span>
            </a>
          )}
          {!isMobile && (
            <a href={allPropertiesHref}>
              <span className="title">All Properties</span>
            </a>
          )}
          {!isMobile && (
            <div className="toogle-menu">
              <a onClick={this.toggleGridicsMenu} className="gridics-menu" href="#gridics-menu">
                {menuCollapsed ? (
                  <div className={classnames('collapsed-title visible')}>
                    <div className={classnames('collapsed-title-logo')}>
                      <img src={logoSrc} title="Gridics" alt="Gridics" width="22" height="22" />
                    </div>
                    <div className={classnames('collapsed-title-text')}>Gridics</div>
                  </div>
                ) : (
                  <div className={classnames('expanded-title visible')}>
                    <span className={classnames('expanded-title-text')}>Powered By:</span>
                    <div className={classnames('expanded-title-logo visible')} />
                  </div>
                )}
              </a>
              <div className={`submenu${!showGridicsMenu ? ' hide' : ''}`}>
                <div className="menu-group">HELP</div>
                <ul>
                  <li>
                    <a href={`${marketingSiteUrl}/team`} target="_blank" rel="noopener noreferrer">
                      About Us
                      <i className="icon-b icon-b-external" />
                    </a>
                  </li>
                  <li>
                    <a href="#feedback-form" onClick={LeftMenu.openFeedbackPopup}>
                      Give Feedback
                    </a>
                  </li>
                </ul>
                <div className="menu-group">LEGAL</div>
                <ul>
                  <li>
                    <a onClick={this.openLegalPopup} href="#legal">
                      Disclaimer
                    </a>
                  </li>
                  <li>
                    <a href={`${marketingSiteUrl}/privacy`} target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                      <i className="icon-b icon-b-external" />
                    </a>
                  </li>
                  <li>
                    <a href={`${marketingSiteUrl}/terms`} target="_blank" rel="noopener noreferrer">
                      Terms
                      <i className="icon-b icon-b-external" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </nav>
      </div>
    );
  }
}

LeftMenu.defaultProps = defaultProps;
LeftMenu.propTypes = propTypes;

const mapStateToProps = state => ({
  pageData: state.doc.pageData,
  enabledLeftOverlay: state.ui.enabledLeftOverlay,
  userData: state.user.userData,
  isMobile: state.ui.isMobile,
  menuCollapsed: state.ui.menuCollapsed,
  openedPropertyState: state.map.openedPropertyState,
  mapSearchResults: state.map.mapSearchResults,
  mapData: state.map.mapData,
  navigationPlace: state.map.navigation.place,
});

const mapDispatchToProps = dispatch => ({
  toggleLeftOverlay: (name, force, updateParam) => dispatch(actionCreators.toggleLeftOverlay(name, force, updateParam)),
  toggleMenuCollapse: state => dispatch(actionCreators.toggleMenuCollapse(state)),
});

export default historyComponent(connect(mapStateToProps, mapDispatchToProps)(LeftMenu));
